import ShowPasswordIcon from "@mui/icons-material/Visibility";
import HidePasswordIcon from "@mui/icons-material/VisibilityOff";
import { IconButton, Skeleton, Stack, TextField, TextFieldProps } from "@mui/material";
import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form";
import FormFieldHint from "./FormFieldHint";

interface Props<T extends FieldValues> extends Omit<TextFieldProps, "name" | "label" | "control"> {
	name: Path<T>;
	label: string;
	isLoading?: boolean;
	hintMessage?: string;
	showPassword?: boolean;
	form: UseFormReturn<T, null>;
	onToggleShowPassword?: VoidFunction;
}

/**
 * FormTextField acts as a wrapper component for text fields used in conjunction with React Hook Form.
 *
 * @param name                 the name of the field bound to this text field
 * @param label                the label of the text field
 * @param isLoading            if true, the component will render a skeleton element
 * @param hintMessage          the optional hint message that may be rendered at the end of the field
 * @param form                 the form reference
 * @param showPassword         if true, the field will show the password
 * @param onToggleShowPassword if defined, the text field will render a button for toggling password visibility
 * @param rest
 * @returns              a TextField wrapped in a React Hook Form Control component
 */
const FormPasswordField = <T extends FieldValues>({
	form,
	name,
	label,
	isLoading,
	hintMessage,
	onToggleShowPassword,
	showPassword = false,
	...rest
}: Props<T>): React.ReactElement => {
	if (isLoading) {
		return <Skeleton height="40px" variant="rounded" className="loading-field" />;
	}

	return (
		<Controller
			name={name}
			control={form.control}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<Stack spacing={1} direction="row">
					<TextField
						{...rest}
						type={passwordFieldType(showPassword)}
						value={value ?? ""}
						label={label}
						size="small"
						error={error !== undefined}
						helperText={error?.message ?? <FormFieldHint text={hintMessage} />}
						slotProps={
							onToggleShowPassword ? passwordFieldProps(onToggleShowPassword, showPassword) : undefined
						}
						onChange={(v) => {
							onChange(v.target.value);
							form.trigger(name);
						}}
					/>
				</Stack>
			)}
		/>
	);
};

/**
 * Returns props to be used on password text fields.
 *
 * @param onClick
 * @param show
 */
const passwordFieldProps = (onClick: () => void, show: boolean) => ({
	input: {
		endAdornment: (
			<IconButton onClick={onClick}>
				{show && <HidePasswordIcon />}
				{!show && <ShowPasswordIcon />}
			</IconButton>
		),
	},
});

/**
 * Returns the text field type of form text fields that are usually treated as form fields.
 * @param visible
 */
const passwordFieldType = (visible: boolean) => (visible ? undefined : "password");

export default FormPasswordField;
